// src/utils/conditionalBranch.js
// ─────────────────────────────────────────────────────────────────────────────
// WHICH BRANCH OF A conditionalGroup IS ACTIVE?
//
// A template's conditionalGroup carries one field list per answer:
//
//   { type: 'conditionalGroup', name: 'geographical_location',
//     dependsOn: 'source_of_access',
//     conditions: { 'Wild (Forest Area)': [ … ], 'Cultivated': [ … ] } }
//
// Every renderer used to answer this question for itself, and every one of them
// answered it with a bare `conditions[value]`. That is an EXACT string match on
// a value that has travelled through a form, a save, a backend and a reload —
// "Wild (Forest area)", "wild (forest area)" or a trailing space is enough to
// miss, and a miss does not degrade: the branch resolves to [], the fields are
// dropped, and the applicant's geographical location silently disappears from
// the preview.
//
// So the question is asked in ONE place, tolerantly, and every renderer asks it
// here. Matching widens in strict order — exact, then whitespace/case, then
// alphanumeric-only, then prefix — and stops at the first hit. It never guesses
// past that: an unmatched value returns no branch rather than picking a branch
// that would print one source's fields under another source's answer.
// ─────────────────────────────────────────────────────────────────────────────

/** "Wild (Forest Area) " → "wild (forest area)" */
const norm = s => String(s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
/** "Wild (Forest Area)" → "wildforestarea" — punctuation-insensitive compare. */
const alnum = s => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The key in `conditions` that answers `value`, or '' when none does.
 *
 * @param {object} conditions  the conditionalGroup's branch map
 * @param {*}      value       the saved answer to `dependsOn`
 * @returns {string} a key of `conditions`, or ''
 */
export function resolveBranchKey(conditions, value) {
  if (!conditions || typeof conditions !== 'object') return '';
  const keys = Object.keys(conditions);
  if (!keys.length) return '';

  if (Object.prototype.hasOwnProperty.call(conditions, value)) return value;

  const nv = norm(value);
  if (!nv) return '';
  const byNorm = keys.find(k => norm(k) === nv);
  if (byNorm) return byNorm;

  const av = alnum(value);
  if (!av) return '';
  const byAlnum = keys.find(k => alnum(k) === av);
  if (byAlnum) return byAlnum;

  /* "Wild" saved against a "Wild (Forest Area)" branch, or the reverse — the
     authority has shortened and lengthened these option labels over time.

     Only when the prefix picks out ONE branch. "Wild" against a form offering
     both "Wild (Forest Area)" and "Wild (Other than Forest Area)" is genuinely
     ambiguous, and answering it by taking whichever key happens to come first
     would print one source's fields under the other source's answer — a wrong
     answer stated confidently, which is worse than no answer. */
  const prefixed = keys.filter(k => {
    const ak = alnum(k);
    return ak && (ak.startsWith(av) || av.startsWith(ak));
  });
  return prefixed.length === 1 ? prefixed[0] : '';
}

/** Read `name` out of a plain object OR a lookup function. */
const readValue = (source, name) =>
  typeof source === 'function' ? source(name) : (source ? source[name] : undefined);

/**
 * The field definitions of the active branch — [] when nothing matches.
 *
 * @param {object} def     the conditionalGroup definition
 * @param {object|Function} source  saved values, as an object or a lookup fn
 */
export function activeBranchFields(def, source) {
  if (!def || !def.conditions) return [];
  const key = resolveBranchKey(def.conditions, readValue(source, def.dependsOn));
  const branch = key ? def.conditions[key] : null;
  return Array.isArray(branch) ? branch : [];
}

/**
 * The heading a conditionalGroup's block should carry.
 *
 * The template states it on the group ("(5) Geographical location of the
 * biological resource"); without it the branch's fields land in the document
 * as a run of unexplained "(a) Forest beat / (b) Division" rows.
 */
export function branchGroupLabel(def) {
  const raw = String(def?.label || def?.sectionName || '').replace(/\s*\*+\s*$/, '').trim();
  if (raw) return raw;
  const name = String(def?.name || '').trim();
  if (!name) return '';
  return name.replace(/[_-]+/g, ' ').replace(/^./, c => c.toUpperCase());
}

export default activeBranchFields;
